import { useState } from "react"
import { BASE_URL } from '../../apiConfig';
import { useAuth } from "../../context/AuthProvider";
import { useNavigate, Link} from "react-router-dom";
import { toast } from 'sonner'; 
import { useLanguage } from "../../context/LanguageProvider";

export default function CreateRoleForm() {
  const { t } = useLanguage();
  const { token } = useAuth();
  const router = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    
    // PRODUCTS
    view_products: false,
    add_product: false,
    edit_product: false,
    delete_product: false,

    // CATEGORIES
    view_categories: false,
    add_category: false,
    edit_category: false,
    delete_category: false,

    // VARIANTS
    view_variants: false,
    add_variant: false,
    edit_variant: false,
    delete_variant: false,

    // FOURNISSEURS
    view_fournisseurs: false,
    add_fournisseur: false,
    edit_fournisseur: false,
    delete_fournisseur: false,

    // ORDERS
    view_orders: false,
    update_order: false,
    delete_order: false,
    scan_order: false,
    send_delivery: false,

    // CONFIRMATORS
    view_confirmator: false,
    add_confirmator: false,
    edit_confirmator: false,
    delete_confirmator: false,
    change_confirmator_password: false,

    // SELLERS
    view_saller: false,
    add_saller: false,
    edit_saller: false,
    delete_saller: false,
    change_saller_password: false,
    update_saller_status: false,

    // CONFIRMATOR INVOICES
    view_confirmator_invoices: false,
    generate_confirmator_invoices: false,
    pay_confirmator_invoices: false,
    delete_confirmator_invoices: false,

    // SELLER INVOICES
    view_saller_invoices: false,
    generate_saller_invoices: false,
    pay_saller_invoices: false,
    delete_saller_invoices: false,

    // STATISTICS
    view_statistics: false,
    
    // ANNONCES
    view_annonces: false,
    add_annonce: false,
    edit_annonce: false,
    delete_annonce: false,

    // RECLAMATIONS
    view_reclamations: false,
    reply_reclamation: false,
    delete_reclamation: false,

    // CONFIG
    edit_config: false,

    // BLACKLIST
    view_blacklist: false,
    add_blacklist: false,
    remove_blacklist: false,
  })

  const [isLoading, setIsLoading] = useState(false)
  const [errors, setErrors] = useState({})
  const [success, setSuccess] = useState(false)

  const formatPermissionName = (permissionKey) => {
    return t(`roles.permission_labels.${permissionKey}`);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    })
    if (errors[name]) {
      setErrors({ ...errors, [name]: "" })
    }
  }

  const handleGroupPermissionChange = (permissions, value) => {
    const updatedData = { ...formData };
    permissions.forEach(permission => {
      updatedData[permission] = value;
    });
    setFormData(updatedData);
  };

  const validateForm = () => {
    const newErrors = {}

    if (!formData.name.trim()) newErrors.name = t('roles.validation.name_required')

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsLoading(true);

    try {
      const res = await fetch(BASE_URL+"/api/role", {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      if (!res.ok) throw new Error("Failed to create role");

      const data = await res.json();
      console.log("Role created:", data);
      setSuccess(true);
      toast.success(t('roles.success_update'));
      setTimeout(() => {
        setFormData({
          name: "",
          view_products: false,
          add_product: false,
          edit_product: false,
          delete_product: false,
          view_categories: false,
          add_category: false,
          edit_category: false,
          delete_category: false,
          view_variants: false,
          add_variant: false,
          edit_variant: false,
          delete_variant: false,
          view_fournisseurs: false,
          add_fournisseur: false,
          edit_fournisseur: false,
          delete_fournisseur: false,
          view_orders: false,
          update_order: false,
          delete_order: false,
          scan_order: false,
          send_delivery: false,
          view_confirmator: false,
          add_confirmator: false,
          edit_confirmator: false,
          delete_confirmator: false,
          change_confirmator_password: false,
          view_saller: false,
          add_saller: false,
          edit_saller: false,
          delete_saller: false,
          change_saller_password: false,
          update_saller_status: false,
          view_confirmator_invoices: false,
          generate_confirmator_invoices: false,
          pay_confirmator_invoices: false,
          delete_confirmator_invoices: false,
          view_saller_invoices: false,
          generate_saller_invoices: false,
          pay_saller_invoices: false,
          delete_saller_invoices: false,
          view_statistics: false,
          view_annonces: false,
          add_annonce: false,
          edit_annonce: false,
          delete_annonce: false,
          view_reclamations: false,
          reply_reclamation: false,
          delete_reclamation: false,
          edit_config: false,
          view_blacklist: false,
          add_blacklist: false,
          remove_blacklist: false,
        });
        router('/roles')
        setSuccess(false);
      }, 1000);
    } catch (error) {
      console.error("Error creating role:", error);
      toast.error(t('roles.errors.update_failed'));
    } finally {
      setIsLoading(false);
    }
  };

  const permissionGroups = {
    "product_management": {
      permissions: [
        "view_products",
        "add_product",
        "edit_product", 
        "delete_product",
      ],
      nameKey: "permission_groups.product_management",
      descKey: "permission_groups.product_management_desc",
    },
    "category_management": {
      permissions: [
        "view_categories",
        "add_category",
        "edit_category",
        "delete_category",
      ],
      nameKey: "permission_groups.category_management",
      descKey: "permission_groups.category_management_desc",
    },
    "variant_management": {
      permissions: [
        "view_variants",
        "add_variant",
        "edit_variant",
        "delete_variant",
      ],
      nameKey: "permission_groups.variant_management",
      descKey: "permission_groups.variant_management_desc",
    },
    "supplier_management": {
      permissions: [
        "view_fournisseurs",
        "add_fournisseur",
        "edit_fournisseur",
        "delete_fournisseur",
      ],
      nameKey: "permission_groups.supplier_management",
      descKey: "permission_groups.supplier_management_desc",
    },
    "order_management": {
      permissions: [
        "view_orders",
        "update_order",
        "delete_order",
        "scan_order",
        "send_delivery",
      ],
      nameKey: "permission_groups.order_management",
      descKey: "permission_groups.order_management_desc",
    },
    "confirmator_management": {
      permissions: [
        "view_confirmator",
        "add_confirmator",
        "edit_confirmator",
        "delete_confirmator",
        "change_confirmator_password",
      ],
      nameKey: "permission_groups.confirmator_management",
      descKey: "permission_groups.confirmator_management_desc",
    },
    "seller_management": {
      permissions: [
        "view_saller",
        "edit_saller",
        "delete_saller",
        "change_saller_password",
        "update_saller_status",
      ],
      nameKey: "permission_groups.seller_management",
      descKey: "permission_groups.seller_management_desc",
    },
    "confirmator_invoices": {
      permissions: [
        "view_confirmator_invoices",
        "generate_confirmator_invoices",
        "pay_confirmator_invoices",
        "delete_confirmator_invoices",
      ],
      nameKey: "permission_groups.confirmator_invoices",
      descKey: "permission_groups.confirmator_invoices_desc",
    },
    "seller_invoices": {
      permissions: [
        "view_saller_invoices",
        "generate_saller_invoices",
        "pay_saller_invoices",
        "delete_saller_invoices",
      ],
      nameKey: "permission_groups.seller_invoices",
      descKey: "permission_groups.seller_invoices_desc",
    },
    "statistics": {
      permissions: [
        "view_statistics",
      ],
      nameKey: "permission_groups.statistics",
      descKey: "permission_groups.statistics_desc",
    },
    "announcement_management": {
      permissions: [
        "view_annonces",
        "add_annonce",
        "edit_annonce",
        "delete_annonce",
      ],
      nameKey: "permission_groups.announcement_management",
      descKey: "permission_groups.announcement_management_desc",
    },
    "reclamation_management": {
      permissions: [
        "view_reclamations",
        "reply_reclamation",
      ],
      nameKey: "permission_groups.reclamation_management",
      descKey: "permission_groups.reclamation_management_desc",
    },
    "system_configuration": {
      permissions: [
        "edit_config",
      ],
      nameKey: "permission_groups.system_configuration",
      descKey: "permission_groups.system_configuration_desc",
    },
    "blacklist_management": {
      permissions: [
        "view_blacklist",
        "add_blacklist",
        "remove_blacklist",
      ],
      nameKey: "permission_groups.blacklist_management",
      descKey: "permission_groups.blacklist_management_desc",
    },
  };

  const getSelectedPermissionsSummary = () => {
    const selected = Object.keys(formData).filter(key =>
      key !== 'name' && formData[key] === true
    );

    if (selected.length === 0) {
      return t('roles.no_permissions_selected');
    } else if (selected.length <= 3) {
      return selected.map(key => formatPermissionName(key)).join(", ");
    } else {
      return `${selected.slice(0, 3).map(key => formatPermissionName(key)).join(", ")}, ${t('roles.and_others')} ${selected.length - 3} ${t('roles.others')}`;
    }
  };

  const isGroupFullySelected = (permissions) => {
    return permissions.every(permission => formData[permission]);
  };

  const isGroupPartiallySelected = (permissions) => {
    return permissions.some(permission => formData[permission]) && 
           !isGroupFullySelected(permissions);
  };

  return (
    <div className="container mx-auto">
      <div className="max-w-4xl mx-auto bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden">
        {success && (
          <div className="bg-[var(--color-primary-500)] text-white p-6 text-center">
            <div className="flex items-center justify-center mb-2">
              <svg className="h-8 w-8 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span className="text-lg font-semibold">{t('roles.success_update')}</span>
            </div>
            <p className="text-[var(--color-primary-50)] text-xs">{t('roles.updated_with_permissions')}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="p-8">
          <div className="mb-8">
            <label htmlFor="name" className="block text-sm font-semibold text-gray-900 mb-3">
              {t('roles.role_name_label')}
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder={t('roles.role_name_placeholder')}
              className={`w-full py-3 px-4 text-xs border-2 outline-none rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white ${
                errors.name
                  ? "border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100"
                  : "border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
              }`}
            />
            {errors.name && <p className="mt-2 text-red-600 text-xs flex items-center">
              <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              {errors.name}
            </p>}
          </div>

          <div className="mb-8">
            <h3 className="text-lg font-bold text-gray-900">{t('roles.assign_permissions')}</h3>
            <p className="text-gray-600 text-xs mb-4">{t('roles.assign_permissions_desc')}</p>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(permissionGroups).map(([groupKey, group]) => (
                <div key={groupKey} className="border border-gray-200 rounded-xl px-3 py-4 bg-gray-50">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h4 className="text-xs font-semibold text-gray-800">{t(group.nameKey)}</h4>
                      <p className="text-[11px] text-gray-600 mt-1">{t(group.descKey)}</p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        type="button"
                        onClick={() => handleGroupPermissionChange(group.permissions, true)}
                        className="text-[11px] cursor-pointer px-2 py-0.5 bg-green-100 text-green-700 rounded-md hover:bg-green-200 transition-colors"
                      >
                        {t('roles.select_all')}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleGroupPermissionChange(group.permissions, false)}
                        className="text-[11px] cursor-pointer px-2 py-0.5 bg-red-100 text-red-700 rounded-md hover:bg-red-200 transition-colors"
                      >
                        {t('roles.select_none')}
                      </button>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-4">
                    {group.permissions.map((permissionKey) => (
                      <div key={permissionKey} className="flex items-center">
                        <input
                          type="checkbox"
                          id={permissionKey}
                          name={permissionKey}
                          checked={formData[permissionKey]}
                          onChange={handleInputChange}
                          className="sr-only"
                        />
                        <label htmlFor={permissionKey} className="flex items-center gap-2 cursor-pointer">
                          <div className={`w-7 h-4 rounded-full transition-all duration-300 flex items-center ${
                            formData[permissionKey] ? 'bg-green-200' : 'bg-gray-200'
                          }`}>
                            <div className={`w-3 h-3 rounded-full shadow-md transition-transform duration-300 ${
                              formData[permissionKey] ? 'translate-x-3.5 bg-green-600' : 'translate-x-0.5 bg-gray-400'
                            }`} />
                          </div>
                          <span className="text-xs text-gray-700">
                            {formatPermissionName(permissionKey)}
                          </span>
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 pt-6 border-t border-gray-200">
            <Link 
              to="/roles" 
              className="flex-1 py-3 px-4 text-xs text-center border-2 border-gray-200 text-gray-700 font-semibold rounded-2xl hover:border-gray-300 hover:bg-gray-50 transition-all duration-200"
            >
              {t('buttons.cancel')}
            </Link>
            <button
              type="submit"
              disabled={isLoading}
              className={`flex-1 cursor-pointer py-3 px-4 text-xs font-semibold rounded-2xl transition-all duration-200 ${
                isLoading
                  ? "bg-gray-400 text-white cursor-not-allowed"
                  : "bg-[var(--color-primary-500)] hover:bg-[var(--color-primary-400)] text-white shadow-lg hover:shadow-xl transform hover:scale-105"
              }`}
            >
              {isLoading ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  {t('roles.saving')}
                </span>
              ) : (
                <span className="flex items-center justify-center">
                  {t('buttons.save')}
                  <svg className="h-4 w-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                </span>
              )}
            </button>
          </div>

          {(formData.name || Object.values(formData).some(val => typeof val === 'boolean' && val)) && (
            <div className="mt-8 p-6 bg-gray-50 rounded-2xl border border-gray-200">
              <h3 className="font-semibold text-gray-900 mb-4">{t('roles.role_preview')}</h3>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[var(--color-primary-50)] rounded-2xl flex items-center justify-center">
                  <svg className="h-6 w-6 text-[var(--color-primary-500)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 14v6m-3-3h6M6 10h2m-2 4h2m-2 4h2M14 4h6V3m-6-1h6v12h-6V2zm-8 4v6h2V4H6zM4 14v6h2v-6H4zm0 0H2" />
                  </svg>
                </div>
                <div className="flex-1">
                  <div className="font-semibold text-lg text-gray-900 mb-1">
                    {formData.name || t('roles.role_name_default')}
                  </div>
                  <div className="text-sm text-gray-600 mb-2">
                    {t('roles.permissions_label')} {getSelectedPermissionsSummary()}
                  </div>
                  <div className="text-xs text-gray-500">
                    {Object.values(formData).filter(val => typeof val === 'boolean' && val).length} {t('roles.permissions_total')}
                  </div>
                </div>
              </div>
            </div>
          )}
        </form>
      </div>
    </div>
  )
}
